import { forwardRef } from 'react';
import type { ReactNode, HTMLProps } from 'react';
import cx from 'clsx';
import { IconChevronLeftOutlined, IconCrossRoundedFilled } from '@repo/monochrome-icons';
import { Button } from 'react-aria-components';
import { useIntl } from 'react-intl';
import ButtonStyles from '../../primitives/button.module.css';
import styles from './filter-button.module.css';

interface FilterButtonProps extends HTMLProps<HTMLDivElement> {
  children: ReactNode;
  onPress?: () => void;
  onClear?: (e: React.MouseEvent) => void;
  hasMenu?: boolean;
  isActive?: boolean;
  isOpen?: boolean;
}

export const FilterButton = forwardRef<HTMLButtonElement, FilterButtonProps>(
  function FilterButton(props, ref): ReactNode {
    const { children, className, onClear, hasMenu, isActive, isOpen, onPress, ...rest } = props;
    const { formatMessage } = useIntl();
    const showCancelButton = isActive && onClear;
    return (
      <div className={cx(styles.wrapper, className)} {...rest}>
        <Button
          className={cx(
            ButtonStyles['btn-filter'],
            styles.btn,
            isActive && ButtonStyles['btn--active'],
            isOpen && styles.isOpen
          )}
          onPress={onPress}
          ref={ref}
        >
          {children}
          {showCancelButton ? (
            <span aria-hidden="true" className={styles['cancel-placeholder']} />
          ) : null}
          {hasMenu && !isActive ? (
            <IconChevronLeftOutlined
              className={cx(isOpen && styles['is-open'])}
              data-test-filter-arrow-icon
            />
          ) : null}
        </Button>
        {showCancelButton ? (
          <button
            aria-label={formatMessage({ id: 'a11y.buttons.close-aria-label' })}
            className={styles['period-cancel-btn']}
            data-test-cancel-button
            onClick={onClear}
            type="button"
          >
            <IconCrossRoundedFilled aria-hidden="true" />
          </button>
        ) : null}
      </div>
    );
  }
);
